import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCKERS = {
  PLATAFORMA_GENERICA_NO_REGISTRADA: "OkPos no aparece en el registro general de plataformas.",
  IDENTIDAD_DE_SERVICIO_NO_CONFIGURADA: "Falta la identidad de servicio exclusiva OkPos ↔ OkBot.",
  CONTRATO_V1_NO_ACEPTADO: "El contrato v1 no ha sido aceptado en OkBot.",
  CONTRATO_REMOTO_OKPOS_NO_CONFIRMADO: "OkPos aún no ha confirmado este contrato mediante una llamada de servicio a OkBot.",
  PERMISOS_DEL_CONTRATO_INCOMPLETOS: "El contrato no incluye todos los permisos operativos.",
  NINGUN_NEGOCIO_VINCULADO: "No hay negocios aprovisionados mediante Connect.",
  FIRMA_DE_AUDITORIA_NO_VERIFICADA: "El receptor de auditoría aún no confirmó una prueba firmada.",
  WEBHOOKS_OPERATIVOS_DESACTIVADOS: "Los callbacks operativos están desactivados.",
  RECEPTOR_OPERATIVO_OKPOS_NO_CERTIFICADO: "Falta confirmar el receptor operativo y sus ACK en OkPos.",
  NEGOCIOS_CON_BLOQUEOS: "Hay negocios sin canal o identidad verificada.",
  NEGOCIO_INACTIVO: "Negocio suspendido.",
  IDENTIDAD_REMOTA_NO_ASIGNADA: "Falta identidad remota.",
  CANAL_NO_VERIFICADO: "Canal Meta/Kapso no verificado para este negocio.",
};

export default function OkPosConnectionPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async (clicked = false) => {
    setBusy(true);
    setError("");
    try {
      const result = clicked
        ? await axios.post(`${API}/admin/connect/okpos/refresh`)
        : await axios.get(`${API}/admin/connect/okpos/readiness`);
      setStatus(result.data);
      if (clicked) {
        toast[result.data.fully_active ? "success" : "info"](
          result.data.fully_active
            ? "Conexión verificada"
            : "Revisión completada: hay requisitos pendientes"
        );
      }
    } catch {
      setError("No se pudo comprobar la conexión. Intenta de nuevo.");
      if (clicked) toast.error("No se pudo actualizar la conexión");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <section className="glass-panel p-5 space-y-4" aria-label="Estado de conexión OkPos y OkBot">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-white font-semibold">OkPos ↔ OkBot</h3>
          <p className="text-gray-400 text-sm mt-1">
            Estado específico de Connect. “Activa” en el registro general no acredita entregas ni negocios listos.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refresh(true)}
          disabled={busy}
          className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50"
          aria-label="Actualiza conexión OkPos"
        >
          <RefreshCw size={15} className={busy ? "animate-spin" : ""} />
          {busy ? "Comprobando..." : "Actualiza conexión"}
        </button>
      </div>

      {error && <p role="alert" className="text-red-300 text-sm">{error}</p>}
      {status && (
        <div aria-live="polite" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 ${
              status.fully_active ? "bg-green-500/15 text-green-300" : "bg-amber-500/15 text-amber-300"
            }`}>
              {status.fully_active ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
              {status.fully_active ? "Verificada" : status.state === "unavailable" ? "No disponible" : "Parcial — no certificada"}
            </span>
            <span className="text-gray-500">
              Registro general: {status.generic_platform?.status === "active" ? "Activa" : "Sin conexión activa"}
            </span>
            <span className="text-gray-500">Negocios vinculados: {status.total_businesses}</span>
            <span className="text-gray-500">
              Revisión: {new Date(status.checked_at).toLocaleString()}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div className="bg-black/20 rounded-lg p-3 text-gray-300">
              Contrato: {status.contract?.accepted ? `v1, revisión ${status.contract.revision}` : "pendiente"}
            </div>
            <div className="bg-black/20 rounded-lg p-3 text-gray-300">
              Firma de auditoría: {status.audit_signature_verified ? "verificada" : "pendiente"}
            </div>
            <div className="bg-black/20 rounded-lg p-3 text-gray-300">
              Eventos por enviar: {(status.outbox?.pending || 0) + (status.outbox?.retry || 0)}
              {status.outbox?.blocked ? ` · bloqueados: ${status.outbox.blocked}` : ""}
            </div>
          </div>
          {status.blockers?.length > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
              <p className="text-amber-200 text-sm font-medium mb-2">Falta para activar la integración</p>
              <ul className="list-disc pl-5 space-y-1 text-gray-300 text-sm">
                {status.blockers.map(code => <li key={code}>{BLOCKERS[code] || code}</li>)}
              </ul>
            </div>
          )}
          {status.businesses?.length > 0 && (
            <div className="space-y-2">
              <p className="text-gray-300 text-sm font-medium">Negocios</p>
              {status.businesses.map(business => (
                <div key={business.business_id} className="bg-black/20 rounded-lg p-3 text-xs text-gray-300">
                  <span className="font-mono">{business.business_id}</span>
                  {" · "}{business.channel || "sin canal"}
                  {" · "}{business.blockers.map(code => BLOCKERS[code] || code).join("; ")}
                </div>
              ))}
              {status.total_businesses > status.businesses.length && (
                <p className="text-gray-500 text-xs">Se muestran los primeros {status.businesses.length} negocios.</p>
              )}
            </div>
          )}
          <p className="text-gray-500 text-xs">
            Actualizar sólo vuelve a comprobar la evidencia. No envía WhatsApp, cambia claves ni habilita receptores que OkPos aún no confirmó.
          </p>
        </div>
      )}
    </section>
  );
}